import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/hooks/useAuth'
import { LoginForm } from '@/types'
import { UserIcon, LockClosedIcon, XMarkIcon } from '@heroicons/react/24/solid'

type Role = 'customer' | 'admin'

export default function Login() {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<Role>('customer')
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const { login, resetPassword } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>()

  const onSubmit = async (data: LoginForm) => {
    setLoading(true)
    try {
      await login(data.email, data.password)
      toast.success('Login successful!')
      // App.tsx handles redirection based on role
    } catch (error: any) {
      toast.error(error.message || `Login failed for ${activeTab}.`)
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!resetEmail.trim()) {
      toast.error('Please enter your email address.')
      return
    }

    setResetLoading(true)
    try {
      await resetPassword(resetEmail)
      toast.success('Password reset email sent! Check your inbox.')
      setShowForgotPassword(false)
      setResetEmail('')
    } catch (error: any) {
      toast.error(error.message || 'Failed to send reset email.')
    } finally {
      setResetLoading(false)
    }
  }

  const renderForm = (role: Role) => {
    const isCustomer = role === 'customer'
    return (
      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label htmlFor={`${role}-email`} className="sr-only">Email address</label>
          <div className="relative">
            <UserIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
            <input
              id={`${role}-email`}
              {...register('email', { required: 'Email is required' })}
              type="email"
              autoComplete="email"
              className="input pl-10"
              placeholder="Email address"
            />
          </div>
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
        </div>
        <div>
          <label htmlFor={`${role}-password`} className="sr-only">Password</label>
          <div className="relative">
            <LockClosedIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
            <input
              id={`${role}-password`}
              {...register('password', { required: 'Password is required' })}
              type="password"
              autoComplete="current-password"
              className="input pl-10"
              placeholder="Password"
            />
          </div>
          {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
        </div>

        {/* Remember Me Checkbox */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id={`${role}-remember`}
              type="checkbox"
              {...register('rememberMe')}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor={`${role}-remember`} className="ml-2 block text-sm text-gray-900">
              Remember me
            </label>
          </div>
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Forgot your password?
          </button>
        </div>

        <button type="submit" disabled={loading} className="btn-primary w-full flex justify-center">
          {loading ? 'Signing in...' : 'Sign In'}
        </button>

        <div className="text-center text-sm">
          {isCustomer ? (
            <p>
              Don't have an account?{' '}
              <Link to="/customer-register" className="font-medium text-blue-600 hover:text-blue-500">
                Sign up here
              </Link>
            </p>
          ) : (
            <p className="text-gray-500">
              Staff and administrators only.
            </p>
          )}
        </div>
      </form>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            className="mx-auto h-24 w-auto"
            src="/assets/logo.png"
            alt="Pro Shipping Solutions Logo"
          />
          <h1 className="mt-6 text-center text-2xl font-semibold text-gray-700">
            Welcome back
          </h1>
          <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="mb-6">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('customer')}
                    className={`${
                      activeTab === 'customer'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm w-1/2`}
                  >
                    Customer
                  </button>
                  <button
                    onClick={() => setActiveTab('admin')}
                    className={`${
                      activeTab === 'admin'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm w-1/2`}
                  >
                    Admin
                  </button>
                </nav>
              </div>
            </div>
            {activeTab === 'customer' ? renderForm('customer') : renderForm('admin')}
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="input w-full"
                    placeholder="Enter your email address"
                    required
                  />
                </div>
                
                <p className="text-sm text-gray-600">
                  We'll send you a link to reset your password.
                </p>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="btn-primary"
                  >
                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
} 